if (!customElements.get("variant-dropdown-td")) {
  customElements.define(
    "variant-dropdown-td",
    class VariantDropdownTraditional extends HTMLElement {
      constructor() {
        super();
        this.dropdownButton = this.querySelector(
          ".wt-product__option__dropdown",
        );
        this.dropdownIcon = this.dropdownButton?.querySelector("svg");
        this.container = this.querySelector(
          ".wt-product__option__body--traditional",
        );
        this.optionList = this.querySelector(".drawer__list");
        this.isOpen = false;
      }

      connectedCallback() {
        if (this.hasAttribute("data-disabled")) return;

        this.handleToggle = this.handleToggle.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleOptionChange = this.handleOptionChange.bind(this);
        this.dropdownButton.addEventListener("click", this.handleToggle);
        this.addEventListener("change", this.handleOptionChange);
        this.addEventListener("focusout", this.handleFocusOut);
        this.addEventListener("keydown", this.handleKeyDown);
        this.prioritizeMarkedSize();
        this.updateTabIndex();
        this.buttonObserver = new MutationObserver(() => this.syncSelectedOption());
        this.buttonObserver.observe(this.dropdownButton, { childList: true, subtree: true });
        this.syncSelectedOption();
      }

      disconnectedCallback() {
        if (this.hasAttribute("data-disabled")) return;

        this.dropdownButton?.removeEventListener("click", this.handleToggle);
        this.removeEventListener("change", this.handleOptionChange);
        this.removeEventListener("focusout", this.handleFocusOut);
        this.removeEventListener("keydown", this.handleKeyDown);
        document.removeEventListener("click", this.handleDocumentClick);
        this.buttonObserver?.disconnect();
      }

      open() {
        if (this.isOpen) return;

        this.isOpen = true;
        this.classList.add("open");
        this.container.classList.add("open");
        this.container.setAttribute("aria-hidden", "false");
        this.dropdownButton.setAttribute("aria-expanded", "true");
        this.dropdownIcon?.classList.add("open");
        this.updateTabIndex();
        document.addEventListener("click", this.handleDocumentClick);
      }

      close({ restoreFocus = false } = {}) {
        if (!this.isOpen) return;

        this.isOpen = false;
        this.classList.remove("open");
        this.container.classList.remove("open");
        this.container.setAttribute("aria-hidden", "true");
        this.dropdownButton.setAttribute("aria-expanded", "false");
        this.dropdownIcon?.classList.remove("open");
        this.updateTabIndex();
        document.removeEventListener("click", this.handleDocumentClick);
        if (restoreFocus) this.dropdownButton.focus();
      }

      handleToggle(event) {
        event.stopPropagation();
        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      }

      handleDocumentClick(event) {
        if (this.optionList.contains(event.target) || !this.contains(event.target)) {
          this.close();
        }
      }

      handleFocusOut(event) {
        if (this.isOpen && !this.contains(event.relatedTarget)) this.close();
      }

      handleKeyDown(event) {
        const option = event.target.closest(".drawer__list__link");

        if (event.key === "Escape" && this.isOpen) {
          event.preventDefault();
          this.close({ restoreFocus: true });
          return;
        }

        if (
          event.target === this.dropdownButton &&
          (event.key === "ArrowDown" || event.key === "ArrowUp")
        ) {
          event.preventDefault();
          this.open();
          const options = this.getEnabledOptions();
          const target = event.key === "ArrowUp" ? options.at(-1) : options[0];
          target?.focus();
          return;
        }

        if (!option || !this.isOpen) return;

        const options = this.getEnabledOptions();
        const currentIndex = options.indexOf(option);
        let nextIndex = currentIndex;

        if (event.key === "ArrowDown") nextIndex = (currentIndex + 1) % options.length;
        if (event.key === "ArrowUp") nextIndex = (currentIndex - 1 + options.length) % options.length;
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = options.length - 1;

        if (nextIndex !== currentIndex) {
          event.preventDefault();
          options[nextIndex]?.focus();
        }

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          option.click();
        }
      }

      handleOptionChange(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== "radio" || !input.checked) return;

        this.syncSelectedOption(input);
      }

      syncSelectedOption(input = this.querySelector('input[type="radio"]:checked')) {
        if (!input) return;

        const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        const selectedLabel = this.dropdownButton.querySelector("[data-selected-option-label]");
        const selectedContent = this.dropdownButton.querySelector(".traditional-dropdown__selected");
        if (!label || !selectedLabel || !selectedContent) return;

        const valueLabel = label.dataset.optionLabel || input.value;
        if (selectedLabel.textContent !== valueLabel) selectedLabel.textContent = valueLabel;

        const badge = label.querySelector("[data-option-badge]");
        const selectedBadge = this.dropdownButton.querySelector("[data-selected-option-badge]");
        if (selectedBadge && (!badge || selectedBadge.textContent !== badge.textContent)) {
          selectedBadge.remove();
        }
        if (badge) {
          if (!this.dropdownButton.querySelector("[data-selected-option-badge]")) {
            const selectedBadge = badge.cloneNode(true);
            selectedBadge.removeAttribute("data-option-badge");
            selectedBadge.setAttribute("data-selected-option-badge", "");
            selectedContent.append(selectedBadge);
          }
        }
      }

      getEnabledOptions() {
        return Array.from(
          this.querySelectorAll(".drawer__list__link"),
        ).filter((label) => !label.control?.classList.contains("disabled"));
      }

      prioritizeMarkedSize() {
        const markedSize = this.optionList.querySelector(
          ".traditional-dropdown__item--marked",
        );
        if (markedSize) this.optionList.prepend(markedSize);
      }

      updateTabIndex() {
        this.querySelectorAll(".drawer__list__link").forEach((option) => {
          option.setAttribute("tabindex", this.isOpen ? "0" : "-1");
        });
      }
    },
  );
}
